import { useEffect, useRef, useState } from "react";
import { CircleCheck } from "lucide-react";
import { SectionCard, type WizardStepContext } from "@/base";
import { StepLoading } from "@/base/wizard/StepLoading";
import { useStepHold } from "@/base/wizard/stepHold";
import { LoadingNote } from "@/components/LoadingNote";
import { buttonClass } from "@/components/buttonStyles";
import { FIELD_ERROR } from "@/components/fieldStyles";
import { CarrierDeclined } from "@/components/submissions/CarrierDeclined";
import { CarrierQuestionFields } from "@/components/submissions/CarrierQuestions";
import { CarrierStepProblem } from "@/components/submissions/CarrierStepProblem";
import { MissingFieldsList } from "@/components/submissions/MissingFieldsList";
import { MissingChecklist } from "@/components/submissions/wc/MissingChecklist";
import { StepLoadProblem } from "@/components/submissions/wc/StepLoadProblem";
import type {
  AnswerSource,
  CarrierAnswerValue,
} from "@/lib/submissions/brokerSubmissions";
import { insuredEditable } from "@/lib/submissions/wc/business";
import {
  askingCarriers,
  carrierErrors,
  carrierNames,
  nothingMoreNeeded,
  rereadCarriers,
  stillNeeded,
  type WcCarrier,
} from "@/lib/submissions/wc/carrierStep";
import type { WcDraft } from "@/lib/submissions/wc/draft";
import { missingFromFieldErrors } from "@/lib/submissions/wc/review";
import { useCarrierStep } from "@/lib/submissions/wc/useCarrierStep";

// The Workers' Comp wizard's last step (BRK-S387, BRK-S393, BRK-S491):
// Carrier-specific questions, then Get Quotes. Opening it sends the
// application to every carrier at once, with no button (`useCarrierStep`,
// PMS-S815): Great American's questions read creates its application there.
// The step shows only what the broker must answer (Nav, 2026-10-28): a card
// for each carrier that asks, one still being sent the application, one whose
// questions couldn't be read, and one that declined on an answer the broker
// can change; the rest in one line. The indications, a manual carrier and a
// decline the broker can't change show on the quotes screen. Get Quotes, which
// replaced Submit, saves each carrier's answers, then sends the application.
//
// A Submit PMS refused lists what it still needs first: the application's
// missing fields, each linked to the page that asks it, which returns here
// once saved; then each carrier's questions left, which land on that carrier.
// Once submitted, the wizard says so on every page, this one included
// (BRK-S307).

export function CarrierQuestionsStep({
  draft,
  setDraft,
  setLoaded,
  errors,
  busy,
}: WizardStepContext<WcDraft>) {
  const step = useCarrierStep(draft, setLoaded);
  const id = draft.submission?.id ?? "";
  const missing = missingFromFieldErrors(errors);
  const needed = stillNeeded(draft.carrierStep, errors);

  // A carrier whose questions changed with the application is read again
  // (PMS-S815), once per refusal.
  const reread = useRef<typeof errors | null>(null);
  const readQuestions = step.readQuestions;
  useEffect(() => {
    if (reread.current === errors) return;
    reread.current = errors;
    for (const carrierId of rereadCarriers(errors)) readQuestions(carrierId);
  }, [errors, readQuestions]);

  // A refusal brings the first question left into view.
  const firstLeft = needed.find((c) => c.questions.length > 0);
  const firstKey = firstLeft
    ? `${firstLeft.carrierId}:${firstLeft.questions[0].questionId}`
    : null;
  useEffect(() => {
    if (!firstKey) return;
    scrollToQuestion(...(firstKey.split(":") as [string, string]));
  }, [firstKey]);

  const setCarrier = (carrierId: string, patch: Partial<WcCarrier>) =>
    setDraft((d) =>
      d.carrierStep?.carriers
        ? {
            ...d,
            carrierStep: {
              ...d.carrierStep,
              carriers: d.carrierStep.carriers.map((c) =>
                c.carrierId === carrierId ? { ...c, ...patch } : c,
              ),
            },
          }
        : d,
    );

  // Ready carriers with answers, opened again to change them.
  const [changing, setChanging] = useState(false);
  const carrierStep = draft.carrierStep;
  const carriers = carrierStep?.carriers ?? [];
  // A carrier PMS's refusal names stays a card, even with nothing left.
  const flagged = new Set(
    carriers
      .filter((c) => {
        const e = carrierErrors(errors, c.carrierId);
        return (
          e.line !== null ||
          Object.keys(e.questions).length > 0 ||
          (changing && (c.set?.questions.length ?? 0) > 0)
        );
      })
      .map((c) => c.carrierId),
  );
  const { cards, ready } = askingCarriers(carriers, flagged);
  // Get Quotes waits while a carrier is still being sent the application, or
  // the markets are still being asked: until PMS-S901, the fan-out is what
  // records Great American's quote request (BRK-S492). The carriers' own
  // read holds through its placeholder.
  useStepHold(
    carriers.some(
      (c) =>
        c.quotingMode === "api" &&
        ((c.set === undefined && c.problem === null) ||
          (c.indication === undefined && !carrierStep?.fanOutProblem)),
    ),
  );
  // With cards, the rest are named in one line; with none, the broker is all
  // set (Nav, 2026-10-28).
  const allSet = cards.length === 0;
  const note =
    !allSet && ready.length > 0
      ? `Nothing more needed from ${carrierNames(ready)}.`
      : null;
  const changeable = ready.some((c) => (c.set?.questions.length ?? 0) > 0);
  return (
    <div className="flex flex-col gap-4">
      {missing.length > 0 && (
        <section
          aria-label="Still needed"
          className="flex flex-col gap-2 rounded-lg border border-brk-danger/40 bg-brk-danger/5 p-3 text-sm"
        >
          <h3 className="font-medium text-brk-text">Still needed</h3>
          <MissingChecklist
            existingAccountId={
              !insuredEditable(draft.account, draft.submission)
                ? draft.account?.id
                : null
            }
            id={id}
            items={missing}
          />
        </section>
      )}
      {needed.length > 0 && (
        <section
          aria-label="Still needed from the carriers"
          className="flex flex-col gap-3 rounded-lg border border-brk-danger/40 bg-brk-danger/5 p-3 text-sm"
        >
          <h3 className="font-medium text-brk-text">
            Still needed from the carriers
          </h3>
          {needed.map((c) => (
            <div key={c.carrierId} className="flex flex-col gap-1">
              <h4 className="font-medium text-brk-text">{c.carrier}</h4>
              <p className="text-brk-dim">{c.line}</p>
              {c.questions.length > 0 && (
                <ul className="flex flex-col gap-1">
                  {c.questions.map((q) => (
                    <li key={q.questionId}>
                      <button
                        type="button"
                        onClick={() =>
                          scrollToQuestion(c.carrierId, q.questionId)
                        }
                        className="text-left text-brk-accent hover:underline"
                      >
                        {q.prompt}
                      </button>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </section>
      )}

      {carrierStep?.problem ? (
        <StepLoadProblem problem={carrierStep.problem} onRetry={step.retry} />
      ) : !carrierStep?.carriers ? (
        <StepLoading label="Getting the carriers…" />
      ) : carrierStep.carriers.length === 0 ? (
        <p className="text-body text-brk-dim">
          No carrier quotes this application online. Get Quotes sends it to
          Signal, which follows up.
        </p>
      ) : (
        <>
          {carrierStep.fanOutProblem && (
            <div
              role="alert"
              className="flex flex-col gap-2 rounded-md border border-brk-border bg-brk-bg px-3 py-2 text-sm text-brk-dim"
            >
              {carrierStep.fanOutProblem.missing ? (
                <div>
                  The carriers can’t price this application until it has:
                  <MissingFieldsList
                    missing={carrierStep.fanOutProblem.missing}
                    accountHint
                    className="mt-1 list-inside list-disc"
                  />
                </div>
              ) : (
                <>
                  <span>{carrierStep.fanOutProblem.message}</span>
                  <button
                    type="button"
                    onClick={step.retryMarkets}
                    className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
                  >
                    Try Again
                  </button>
                </>
              )}
            </div>
          )}
          {cards.map((c) => (
            <CarrierSection
              key={c.carrierId}
              carrier={c}
              errors={carrierErrors(errors, c.carrierId)}
              busy={busy}
              open={changing}
              onAnswer={(questionId, value) =>
                setCarrier(c.carrierId, {
                  values: { ...c.values, [questionId]: value },
                })
              }
              onSource={(source) => setCarrier(c.carrierId, { source })}
              onRetryQuestions={() => step.readQuestions(c.carrierId)}
            />
          ))}
          {allSet ? (
            <SectionCard>
              <div
                data-all-set
                className="flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-between"
              >
                <div className="flex items-start gap-3">
                  <CircleCheck
                    aria-hidden="true"
                    className="mt-0.5 h-5 w-5 shrink-0 text-green-700 dark:text-green-300"
                  />
                  <div className="flex flex-col gap-1">
                    <p className="font-medium text-brk-text">You’re all set</p>
                    <p className="text-body text-brk-dim">
                      The carriers have everything they need. Press Get Quotes
                      to see your quotes.
                    </p>
                  </div>
                </div>
                {changeable && (
                  <ChangeAnswers onClick={() => setChanging(true)} />
                )}
              </div>
            </SectionCard>
          ) : (
            note && (
              <div className="flex flex-col gap-2 text-body sm:flex-row sm:items-center sm:gap-3">
                <p className="text-brk-text">{note}</p>
                {changeable && (
                  <ChangeAnswers onClick={() => setChanging(true)} />
                )}
              </div>
            )
          )}
        </>
      )}
    </div>
  );
}

/** Opens the answers already given, to change them. */
function ChangeAnswers({ onClick }: { onClick: () => void }) {
  return (
    <button
      type="button"
      onClick={onClick}
      className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start sm:self-auto`}
    >
      Change Answers
    </button>
  );
}

/** Where a carrier's question is on the page. */
function questionAnchor(carrierId: string, questionId: string) {
  return document.querySelector<HTMLElement>(
    `[data-carrier="${CSS.escape(carrierId)}"] [data-question="${CSS.escape(questionId)}"]`,
  );
}

function scrollToQuestion(carrierId: string, questionId: string) {
  const el = questionAnchor(carrierId, questionId);
  el?.scrollIntoView?.({ behavior: "smooth", block: "center" });
  el?.querySelector<HTMLElement>("input, select, textarea")?.focus({
    preventScroll: true,
  });
}

/** One carrier that asks something, or is being asked: its questions, a
 *  decline the broker can change, or its read on the way or failed. `open`
 *  shows answers already given, to change them. */
function CarrierSection({
  carrier: c,
  errors,
  busy,
  open,
  onAnswer,
  onSource,
  onRetryQuestions,
}: {
  carrier: WcCarrier;
  errors: { line: string | null; questions: Record<string, string> };
  busy: boolean;
  open: boolean;
  onAnswer: (questionId: string, value: CarrierAnswerValue) => void;
  onSource: (source: AnswerSource) => void;
  onRetryQuestions: () => void;
}) {
  // A carrier with nothing left shows its answers on request.
  const [reviewing, setReviewing] = useState(false);
  const set = c.set;
  const done = nothingMoreNeeded(c);
  const showFields =
    !!set &&
    !set.declined &&
    set.questions.length > 0 &&
    (!done ||
      reviewing ||
      open ||
      errors.line !== null ||
      Object.keys(errors.questions).length > 0);

  return (
    <div data-carrier={c.carrierId}>
      <SectionCard title={c.carrier} titleAs="h3">
        <div className="flex flex-col gap-3">
          {errors.line && (
            <p role="alert" className={FIELD_ERROR}>
              {errors.line}
            </p>
          )}
          {c.problem ? (
            <CarrierStepProblem
              problem={c.problem}
              carrier={c.carrier}
              onRetry={onRetryQuestions}
            />
          ) : set === undefined ? (
            // A separate call per carrier: a spinner says why it waits
            // (BRK-S488).
            <LoadingNote
              label={`Sending the application to ${c.carrier}…`}
              className="text-sm text-brk-dim"
            />
          ) : set?.declined ? (
            // One that declined on an answer can have it changed; one PMS
            // ruled out asks nothing (BRK-S370).
            <>
              <CarrierDeclined
                carrier={c.carrier}
                reasons={set.declined.reasons}
                next={
                  set.questions.length > 0
                    ? "If an answer was wrong, change it, then Get Quotes."
                    : null
                }
              />
              {set.questions.length > 0 &&
                (reviewing || open ? (
                  <CarrierQuestionFields
                    questions={set.questions}
                    values={c.values}
                    onChange={onAnswer}
                    source={c.source}
                    onSourceChange={onSource}
                    errors={errors.questions}
                    disabled={busy}
                  />
                ) : (
                  <button
                    type="button"
                    onClick={() => setReviewing(true)}
                    className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
                  >
                    Change Answers
                  </button>
                ))}
            </>
          ) : (
            <>
              {done && (
                <div className="flex flex-col gap-1 text-sm sm:flex-row sm:items-center sm:gap-3">
                  <p className="text-brk-text">Nothing more needed from you.</p>
                  {set && set.questions.length > 0 && !showFields && (
                    <button
                      type="button"
                      onClick={() => setReviewing(true)}
                      className={`${buttonClass({ variant: "secondary", size: "sm" })} self-start`}
                    >
                      Change Answers
                    </button>
                  )}
                </div>
              )}
              {showFields && set && (
                <div className="flex flex-col gap-3">
                  <CarrierQuestionFields
                    questions={set.questions}
                    values={c.values}
                    onChange={onAnswer}
                    source={c.source}
                    onSourceChange={onSource}
                    errors={errors.questions}
                    disabled={busy}
                  />
                </div>
              )}
            </>
          )}
        </div>
      </SectionCard>
    </div>
  );
}
